/**
 * L3ApprovedBurst — feiringen `godkjent`: en hel skjerm med poengene, hvem som
 * godkjente hva, saldo før → etter og streaken, og «Fortsett» (#155, redesign
 * steg 5b; beslutning D7 i design/MAPPING.md). Opprinnelig Plan 02-15 /
 * UI-SPEC §11, der L3 var konfetti + count-up + en toast i 3 s.
 *
 * Tier L3 fires on `chore.approved` for OWN chore. Composes:
 *   - <ConfettiBurst tier="l3"> — 60 phone / 140 tablet, 1200ms
 *   - skjermen fra Feiring-skissen: «+N poeng»-skiven, «Bra jobba, {nick}!»,
 *     «{approver} godkjente «{title}»», saldo-chipen og streak-chipen
 *   - PointBalance count-up triggered via CustomEvent so WalletRoute
 *     passes countUpFrom={prevBalance} to its <PointBalance>
 *
 * Godkjenneren og tittelen kommer ikke i WS-rammen (den har bare id-er), så
 * CelebrationProvider slår dem opp på klienten og sender dem inn. Mangler
 * en av dem, faller teksten tilbake til en kortere setning; skjermen vises
 * uansett. Toasten er borte: skjermen bærer nå teksten, og under redusert
 * bevegelse er det skjermen uten animasjon, konfetti og lyd (lyden dempes i
 * audio-porten) — INV-RM-1 collapses motion, not information.
 *
 * Skjermen blir stående til barnet trykker «Fortsett», trykker et annet sted
 * eller trykker Escape (TakeoverScrim). Da kalles `onComplete`, og
 * CelebrationProvider viser neste godkjenning i køen.
 *
 * The count-up event surface keeps this tier decoupled from WalletRoute
 * internals. WalletRoute is the production listener as of issue #79; it holds
 * the event as an intent and applies it only once the balance query has caught
 * up to `to`, because the celebration frame and the points frame arrive on two
 * separate sockets and either can win.
 *
 * The side effect lives in ONE effect keyed on `[instanceId]` alone, and issue
 * #81 is the reason: one approval is one celebration and nothing else may
 * re-run it. CelebrationProvider passes a fresh `() => removeEvent(ev)` arrow on
 * every render, and `nick` / the looked-up names can land mid-celebration, so
 * none of them may sit in that dep array. The effect is once-per-instanceId,
 * guarded by a ref, and MUST NEVER grow a cleanup. The guard is safe only in an
 * effect that has none — cf. ConfettiBurst.tsx, which may ref-guard for exactly
 * the same reason. (Before #155 there was a second, timer-owning lifetime
 * effect that must never be ref-guarded; the 3 s lifetime is gone, so it is
 * too.)
 *
 * Note that eslint here loads no react-hooks plugin: nothing mechanical stops
 * a future edit from re-widening the dep array or adding a cleanup. This
 * comment is the guard, and the issue #81 tests are the net.
 */
import { useEffect, useId, useRef, type MouseEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { Button } from '../../components/primitives/Button';
import { ConfettiBurst } from '../../components/primitives/ConfettiBurst';
import { GLASS_SURFACE } from '../../components/primitives/Card';
import { Icon } from '../../components/primitives/Icon';
import { TakeoverScrim } from '../../components/primitives/TakeoverScrim';
import { TIMINGS, type ViewportClass } from './timings';

interface L3ApprovedBurstProps {
  instanceId: string;
  points: number;
  /** Viewer's nickname used for the "Bra jobba, {nick}!" copy. May be empty when unresolved. */
  nick: string;
  prevBalance: number | undefined;
  /** Navnet på den voksne som godkjente; tomt når det ikke er funnet. */
  approverName?: string | undefined;
  /** Oppgavetittelen; tom når den ikke er funnet. */
  title?: string | undefined;
  /** Dagens streak; 0 eller ukjent skjuler chipen. */
  streakDays?: number | undefined;
  viewport: ViewportClass;
  reducedMotion: boolean;
  onComplete: () => void;
}

export function L3ApprovedBurst({
  instanceId,
  points,
  nick,
  prevBalance,
  approverName = '',
  title = '',
  streakDays = 0,
  viewport,
  reducedMotion,
  onComplete,
}: L3ApprovedBurstProps): ReactNode {
  const { t } = useTranslation();
  const headingId = useId();
  const continueRef = useRef<HTMLButtonElement | null>(null);

  // Latest-ref, so dismissing never closes over a stale provider arrow.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const firedFor = useRef<string | null>(null);

  // The celebration's one side effect: the count-up, ONE per mount. See the
  // header: this effect MUST NEVER grow a cleanup, which is what makes the
  // ref-guard safe. It reads points/prevBalance from the mount-time closure
  // deliberately.
  //
  // "Per mount" is also "per approval": CelebrationProvider keys these
  // children on the event's own id (issue #82, useCelebration.ts
  // `nextEventId`), so a mount corresponds to exactly one approval. The guard
  // below is what makes that hold; the key is what makes the guard reachable.
  useEffect(() => {
    if (firedFor.current === instanceId) return;
    firedFor.current = instanceId;

    // Count-up event for any mounted PointBalance instance keyed to this
    // viewer. WalletRoute reads detail.from / detail.to and passes
    // countUpFrom to its <PointBalance>. No memberId is needed in the detail:
    // useCelebration only dispatches L3 for `claimed_by === viewerId`, so the
    // event is always about the viewer.
    if (prevBalance !== undefined) {
      try {
        const ev = new CustomEvent('stell:countup', {
          detail: { instanceId, from: prevBalance, to: prevBalance + points },
        });
        document.dispatchEvent(ev);
      } catch {
        /* ignore */
      }
    }
  }, [instanceId]);

  // Fokus på «Fortsett», så skjermleser og tastatur står i dialogen.
  useEffect(() => {
    continueRef.current?.focus({ preventScroll: true });
  }, []);

  const dismiss = (): void => onCompleteRef.current();
  const onContinue = (e: MouseEvent): void => {
    e.stopPropagation();
    dismiss();
  };

  const discAnim = reducedMotion
    ? { initial: { scale: 1 }, animate: { scale: 1 } }
    : {
        initial: { scale: TIMINGS.l3.discPop.from },
        animate: { scale: TIMINGS.l3.discPop.to },
        transition: { type: 'spring' as const, ...TIMINGS.l3.discPop.spring },
      };

  const heading = nick
    ? t('celebrations.l3.headingWithNick', { nick })
    : t('celebrations.l3.heading');
  // Setningen under overskriften, etter hva klienten fant.
  const approvedLine =
    approverName && title
      ? t('celebrations.l3.approvedBy', { approver: approverName, title })
      : title
        ? t('celebrations.l3.approvedTitle', { title })
        : approverName
          ? t('celebrations.l3.approvedByOnly', { approver: approverName })
          : t('celebrations.l3.approvedPlain');

  return (
    <TakeoverScrim open onDismiss={dismiss} variant="screen" labelledBy={headingId}>
      <div
        data-testid="l3-approved"
        data-instance-id={instanceId}
        data-reduced-motion={reducedMotion ? 'true' : 'false'}
        className="relative flex min-h-dvh flex-col items-center px-6 pt-28 pb-[max(2.25rem,env(safe-area-inset-bottom))] text-center"
      >
        <div aria-hidden className="pointer-events-none absolute inset-0 bg-burst" />
        <div className="relative flex w-full max-w-sm flex-col items-center">
          <motion.div
            {...discAnim}
            aria-hidden
            className="flex h-50 w-50 items-center justify-center rounded-full bg-lime-bg"
          >
            <span className="flex h-39 w-39 flex-col items-center justify-center rounded-full bg-fab text-on-fab shadow-fab">
              <span className="text-display-m leading-none font-extrabold tracking-tighter tnum">
                +{points}
              </span>
              <span className="text-body font-extrabold">{t('celebrations.l3.points')}</span>
            </span>
          </motion.div>

          <h1 id={headingId} className="m-0 mt-7.5 mb-1.5 text-3xl font-extrabold tracking-tight text-ink">
            <span className="sr-only">{t('celebrations.l3.pointsSr', { count: points })} </span>
            {heading}
          </h1>
          <p data-testid="l3-approved-line" className="m-0 text-base text-ink-2">
            {approvedLine}
          </p>

          <div className="mt-5.5 flex flex-wrap justify-center gap-2.5">
            {prevBalance !== undefined ? (
              <span
                data-testid="l3-balance"
                className={`${GLASS_SURFACE} inline-flex h-10 items-center gap-2 rounded-pill px-4 text-body-s font-bold text-ink`}
              >
                <span className="sr-only">
                  {t('celebrations.l3.balanceSr', { from: prevBalance, to: prevBalance + points })}
                </span>
                <span aria-hidden className="inline-flex items-center gap-2">
                  {t('celebrations.l3.balance')}
                  <span className="text-ink-2 tnum">{prevBalance}</span>
                  <Icon.ChevR size={16} />
                  <strong className="tnum">
                    {t('celebrations.l3.balanceTo', { to: prevBalance + points })}
                  </strong>
                </span>
              </span>
            ) : null}
            {streakDays > 0 ? (
              <span
                data-testid="l3-streak"
                className="inline-flex h-10 items-center gap-1.5 rounded-pill bg-lime-bg px-4 text-body-s font-extrabold text-lime"
              >
                <Icon.Flame size={16} />
                <span>
                  <span className="tnum">{streakDays}</span>{' '}
                  {t('streaks.daysOnRow.unit', { count: streakDays })}
                </span>
              </span>
            ) : null}
          </div>
        </div>

        <ConfettiBurst tier="l3" viewport={viewport} suppressed={reducedMotion} />

        <div className="relative mt-auto w-full max-w-sm pt-10">
          <Button
            ref={continueRef}
            variant="solid"
            size={56}
            className="w-full"
            data-testid="l3-continue"
            onClick={onContinue}
          >
            {t('common.continue')}
          </Button>
        </div>
      </div>
    </TakeoverScrim>
  );
}
